import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import moment from "moment"
import { Grid, Tooltip, Box, Button, Typography, IconButton, ListItem, ListItemText, ListItemIcon, Snackbar } from "@material-ui/core"
import { makeStyles, withStyles, useTheme } from "@material-ui/core/styles"
import TableView from "../components/common/TableView/TableView"
import UserAddEditModel from "../components/user/UserAddEditModel"
import WithDrawer from "../components/common/drawer/withDrawer"
import DeleteUserSVG from "../components/icons/DeleteUserSVG"
import EditUserSVG from "../components/icons/EditUserSVG"
import ConfirmationDialog from "../components/common/dialog/ConfirmationDialog"
import { createUserReset, deleteUser, deleteUserReset, getCaseAccessList, getUsersList, updateUserReset } from "../actions/usersActions"
import TooltipDialog from "../components/common/dialog/Simple"
import { KeyboardArrowDown, FiberManualRecord } from "@material-ui/icons"
import Notification from "../components/Notification/Notification"

const useStyles = makeStyles((theme) => ({
	actionDiv: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-end",
	},
	primaryBtn: {
		textTransform: "initial",
		fontSize: theme.typography.fontSize,
		marginRight: "3px",
		background: theme.palette.primary.dark,
	},
	table: {
		padding: theme.spacing(6, 0),
	},
	header: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: theme.spacing(2, 1),
		paddingTop: "35px",
	},
	headerTitle: theme.typography.h5,
	tooltipDialog: {
		background: theme.palette.grey[50],
		"& .MuiListItemText-primary": {
			fontSize: theme.typography.fontSize,
			textTransform: "capitalize",
		},
		"& .MuiListItem-root": {
			padding: 0,
		},
		"& .MuiListItemIcon-root": {
			minWidth: "20px",
			color: theme.palette.grey[300],
		},
		"& .MuiSvgIcon-fontSizeSmall": {
			fontSize: theme.typography.fontSize,
		},
	},
}))

const PageUserManagement = () => {
	const classes = useStyles()
	const [t] = useTranslation("global")
	const [openUserModel, setOpenUserModel] = useState(false)
	const [userEditData, setUserEditData] = useState({})
	const [open, setOpen] = useState(false)
	const [tooltipDialog, setTooltipDialog] = useState(false)
	const [tooltipDialogData, setTooltipDialogData] = useState([])
	const [tooltipTitle, setTooltipTitle] = useState("")
	const [userDeleteData, setUserDeleteData] = useState({})

	const dispatch = useDispatch()
	const theme = useTheme()

	const { usersList, isUsersListLoading, isUserDeleted, caseList } = useSelector((state) => state.userManagement)
	const { multiBaseList } = useSelector((state) => state.multiBase)

	const getActionItems = () => {
		return (
			<div className={classes.actionDiv}>
				<section>
					<Button
						variant="contained"
						color="primary"
						className={classes.primaryBtn}
						onClick={(e) => {
							handleUserCreation(e)
						}}
					>
						{t("user.list.createNewUser")}
					</Button>
				</section>
			</div>
		)
	}

	const getUserAction = (params) => {
		return (
			<Box>
				<IconButton onClick={() => handleEdit(params?.row)}>
					<EditUserSVG />
				</IconButton>
				<IconButton style={{ p: 0 }} onClick={() => handleDelete(params?.row)}>
					<DeleteUserSVG />
				</IconButton>
			</Box>
		)
	}

	const getRows = () => {
		let userSRows = []
		if (usersList?.length > 0) {
			usersList?.map((user, index) => {
				userSRows?.push({ ...user, id: user?.userid })
			})
		}
		return userSRows
	}

	const handleClose = (status = false) => {
		if (status === true) {
			dispatch(getUsersList())
		}

		setOpenUserModel(false)
	}

	const handleUserCreation = (e) => {
		dispatch(createUserReset())
		setOpenUserModel(true)
		setUserEditData({})
	}

	const handleEdit = (row) => {
		setUserEditData(row)
		setOpenUserModel(true)
	}

	const handleDelete = (row) => {
		dispatch(deleteUserReset())
		setOpen(true)
		setUserDeleteData({ userId: row?.id, userName: `${row?.first_name} ${row?.last_name}` })
	}

	const onUserDeleteComfirm = () => {
		if (userDeleteData?.userId) {
			dispatch(deleteUser({ userid: userDeleteData?.userId }))
			setOpen(false)
		}
	}

	const onDismiss = () => {
		setOpen(false)
	}

	const getFullName = (row) => {
		const fullName = `${row?.first_name} ${row?.last_name}`
		return (
			<Tooltip title={fullName} placement="top">
				<Box style={{ display: "flex", alignItems: "center", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
					<Typography noWrap style={{ textTransform: "capitalize", fontSize: theme.typography.fontSize }}>
						{fullName}
					</Typography>
				</Box>
			</Tooltip>
		)
	}

	const getDataWithInfo = (value) => {
		return (
			<Tooltip title={value?.toString()} placement="top">
				<Box style={{ display: "flex", alignItems: "center", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
					<Typography variant="body2" noWrap>
						{value?.toString()}
					</Typography>
				</Box>
			</Tooltip>
		)
	}

	const getProjectNameList = (projectIds) => {
		let projectList = []
		if (projectIds?.length > 0) {
			for (let i = 0; i < projectIds?.length; i++) {
				if (caseList?.length > 0) {
					const found = caseList.filter((project) => project?.project_id === projectIds[i])
					found?.[0]?.friendly_name ? projectList.push(found?.[0]?.friendly_name) : ""
				}
			}
		}
		return projectList
	}

	const setCaseDialogData = (caseData) => {
		setTooltipDialog(true)
		setTooltipDialogData(caseData)
		setTooltipTitle(t("user.list.tableHeader.caseAccess"))
	}

	const getCreationDate = (dateTime) => {
		return dateTime ? moment(dateTime)?.format("DD/MM/YYYY") : ""
	}

	const getProjectData = (value) => {
		const caseList = getProjectNameList(value)
		return (
			<>
				<Typography
					variant="body2"
					onClick={() => {
						setCaseDialogData(caseList)
					}}
					style={{ cursor: "pointer", minWidth: 158 }}
				>
					{caseList?.length > 1 ? `${caseList?.[0]}...` : caseList?.toString()}
				</Typography>
				{caseList?.length > 1 && (
					<KeyboardArrowDown
						onClick={() => {
							setCaseDialogData(caseList)
						}}
						style={{ marginLeft: 4, cursor: "pointer" }}
					/>
				)}
			</>
		)
	}

	const getColumns = () => {
		return [
			{
				field: "first_name",
				headerName: t("user.list.tableHeader.name"),
				sortable: false,
				resizable: true,
				flex: 1.2,
				renderCell: (params) => getFullName(params?.row),
			},
			{
				field: "email",
				headerName: t("user.list.tableHeader.email"),
				flex: 1,
				sortable: false,
			},
			{
				field: "roles",
				headerName: t("user.list.tableHeader.role"),
				flex: 1.4,
				sortable: false,
				renderCell: (params) => getDataWithInfo(params?.row?.roles),
			},
			{
				field: "projects",
				flex: 1.2,
				headerName: t("user.list.tableHeader.caseAccess"),
				sortable: false,
				renderCell: (params) => getProjectData(params?.row?.projects),
			},
			{
				field: "creation_time",
				flex: 1,
				headerName: t("user.list.tableHeader.createdOn"),
				sortable: false,
				renderCell: (params) => getCreationDate(params?.row?.creation_time),
			},
			{
				field: "action",
				flex: 0.8,
				headerName: t("user.list.tableHeader.action"),
				sortable: false,
				renderCell: getUserAction,
			},
		]
	}

	useEffect(() => {
		if (_.isEmpty(usersList)) {
			dispatch(getUsersList())
		}
	}, [usersList])

	useEffect(() => {
		if (_.isEmpty(caseList)) {
			dispatch(getCaseAccessList())
		}
	}, [caseList])

	useEffect(() => {
		if (isUserDeleted) {
			dispatch(getUsersList())
		}
	}, [isUserDeleted])

	useEffect(() => {
		return () => {
			dispatch(createUserReset())
			dispatch(deleteUserReset())
			dispatch(updateUserReset())
		}
	}, [])

	return (
		<Grid container>
			<Grid item xs={12} data-testid="title-section" className={classes.header}>
				<Typography variant="h2" className={classes.headerTitle}>
					{t("user.list.userManagement")}
				</Typography>
				{getActionItems()}
			</Grid>
			<Grid item xs={12} className={classes.table}>
				<TableView
					columns={getColumns()}
					rows={getRows()}
					isLoading={isUsersListLoading}
					pageInfo={{
						page: 1,
						pageSize: getRows()?.length,
						rowsPerPageOptions: [5, 10, 20],
						totalRowCount: getRows()?.length,
						paginationMode: "server",
						handlePageChange: () => {},
						handlePageSizeChange: () => {},
					}}
					sortInfo={{
						sortingMode: "server",
						sortModel: [],
						onSortModelChange: () => {},
					}}
					checkboxSelection={false}
					footerInfo={{
						hideFooter: true,
						hideSelectionCount: true,
						hidePagination: false,
					}}
					topBarInfo={{
						show: false,
						leftSection: "",
						rightSection: "",
					}}
					totalCount={getRows()?.length}
					showDownloadButton={false}
					showColumnsButton={false}
				/>
			</Grid>
			<UserAddEditModel open={openUserModel} onClose={handleClose} userData={userEditData} />
			<ConfirmationDialog
				open={open}
				title={t("user.delete.title")}
				message={t("user.delete.dialogMessage", { userName: userDeleteData?.userName })}
				onConfirm={onUserDeleteComfirm}
				onDismiss={onDismiss}
				btnText={t("confirmationDialog.comment.delete")}
			/>
			<TooltipDialog
				open={tooltipDialog}
				title={tooltipTitle}
				onClose={() => {
					setTooltipDialog(false)
				}}
			>
				<Box className={classes.tooltipDialog}>
					{tooltipDialogData?.map((item, index) => {
						return (
							<ListItem key={`${item}-${index}`}>
								<ListItemIcon>
									<FiberManualRecord fontSize="small" />
								</ListItemIcon>
								<ListItemText primary={item} />
							</ListItem>
						)
					})}
				</Box>
			</TooltipDialog>
			<Notification />
		</Grid>
	)
}

export default WithDrawer(PageUserManagement)
